// UGC Videos Section with Swiper Integration
class UGCVideoSectionSwiper {
    constructor() {
        this.videosSwiper = null;
        this.reelsSwiper = null;
        this.currentVideoIndex = 0;
        this.autoPlayTimer = null;
        this.isAutoPlaying = false;
        this.videos = [];
        this.reelsModal = null;
        this.currentReelIndex = 0;
        this.videoData = [];
        this.observerOptions = {
            threshold: 0.3,
            rootMargin: '0px 0px -20% 0px'
        };

        this.init();
    }

    init() {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        console.log('Initializing UGC Video Section with Swiper');

        // Get DOM elements
        this.videos = document.querySelectorAll('.preview-video');
        this.videoCards = document.querySelectorAll('.video-card');
        this.reelsModal = document.getElementById('reelsModal');

        // iOS fix: iPhones force videos into native fullscreen on .play()
        // unless playsinline is set. Also require muted for inline autoplay.
        this.videos.forEach(video => {
            video.setAttribute('playsinline', '');
            video.setAttribute('webkit-playsinline', '');
            video.setAttribute('muted', '');
            video.muted = true;
            video.playsInline = true;
        });

        // Extract video data
        this.extractVideoData();

        // Initialize Swipers
        this.initVideosSwiper();
        this.initReelsSwiper();

        // Setup intersection observer for lazy loading
        this.setupIntersectionObserver();

        // Setup event listeners
        this.setupEventListeners();

        console.log('UGC Video Section initialized successfully');
    }

    extractVideoData() {
        this.videoData = Array.from(this.videos).map((video, index) => ({
            id: index,
            fullSrc: video.dataset.fullSrc || video.querySelector('source').src,
            ugcVideo: video.dataset.ugc,
            element: video
        }));

        console.log('Extracted video data:', this.videoData);
    }

    initVideosSwiper() {
        this.videosSwiper = new Swiper('.videos-swiper', {
            slidesPerView: 2,
            spaceBetween: 16,
            centeredSlides: true,
            autoplay: {
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                480: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                    autoplay: {
                        delay: 4000,
                        pauseOnMouseEnter: false
                    },
                    centeredSlides: false
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 16,
                    autoplay: {
                        delay: 12000
                    },
                    centeredSlides: false
                },
                1024: {
                    slidesPerView: 4,
                    spaceBetween: 16,
                    autoplay: {
                        delay: 16000
                    },
                    centeredSlides: false
                },
                1200: {
                    slidesPerView: 4,
                    spaceBetween: 16,
                    autoplay: {
                        delay: 16000
                    },
                    centeredSlides: false
                }
            },
            on: {
                init: () => {
                    console.log('Videos Swiper initialized');
                },
                slideChange: () => {
                    this.handleSlideChange();
                },
                autoplayStart: () => {
                    console.log('Swiper autoplay started');
                },
                autoplayStop: () => {
                    console.log('Swiper autoplay stopped');
                }
            }
        });
    }

    initReelsSwiper() {
        const slideCount = this.videoData.length;
        this.reelsSwiper = new Swiper('.reels-swiper', {
            direction: 'vertical',
            slidesPerView: 1,
            spaceBetween: 5,
            loop: true,
            speed: 300,
            touchRatio: 1,
            touchAngle: 45,
            grabCursor: true,
            loopedSlides: slideCount,
            loopAdditionalSlides: slideCount,
            navigation: {
                nextEl: '#reelsNext',
                prevEl: '#reelsPrev',
            },
            mousewheel: {
                enabled: true,
                forceToAxis: true,
                sensitivity: 1,
                thresholdDelta: 50
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true
            },
            on: {
                init: () => {
                    console.log('Reels Swiper initialized');
                },
                slideChange: () => {
                    this.handleReelsSlideChange();
                }
            }
        });
    }

    setupIntersectionObserver() {
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.isAutoPlaying) {
                        console.log('UGC section came into view, starting video auto-play');
                        this.startVideoAutoPlay();
                    } else if (!entry.isIntersecting && this.isAutoPlaying) {
                        console.log('UGC section out of view, stopping video auto-play');
                        this.stopVideoAutoPlay();
                    }
                });
            }, this.observerOptions);

            const ugcSection = document.getElementById('ugc-videos');
            if (ugcSection) {
                observer.observe(ugcSection);
            }
        } else {
            // Fallback for browsers without IntersectionObserver
            console.log('IntersectionObserver not supported, starting auto-play immediately');
            setTimeout(() => this.startVideoAutoPlay(), 1000);
        }
    }

    setupEventListeners() {
        // Video card click events
        this.videoCards.forEach((card, index) => {
            card.addEventListener('click', (e) => {
                e.preventDefault();
                this.openReelsModal(index);
            });

            // Add keyboard accessibility
            card.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openReelsModal(index);
                }
            });

            // Make cards focusable
            card.setAttribute('tabindex', '0');
            card.setAttribute('role', 'button');
            card.setAttribute('aria-label', `Play video ${index + 1}`);
        });

        // Reels modal close events
        const closeBtn = document.getElementById('closeReels');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeReelsModal());
        }

        const modalOverlay = document.querySelector('.modal-overlay');
        if (modalOverlay) {
            modalOverlay.addEventListener('click', () => this.closeReelsModal());
        }

        // Reels navigation
        // const reelsPrev = document.getElementById('reelsPrev');
        // const reelsNext = document.getElementById('reelsNext');

        // if (reelsPrev) {
        //     reelsPrev.addEventListener('click', () => {
        //         if (this.reelsSwiper) {
        //             this.reelsSwiper.slidePrev();
        //         }
        //     });
        // }

        // if (reelsNext) {
        //     reelsNext.addEventListener('click', () => {
        //         if (this.reelsSwiper) {
        //             this.reelsSwiper.slideNext();
        //         }
        //     });
        // }

        // Keyboard events for modals and navigation
        document.addEventListener('keydown', (e) => {
            if (this.reelsModal && this.reelsModal.classList.contains('active')) {
                switch (e.key) {
                    case 'Escape':
                        this.closeReelsModal();
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        if (this.reelsSwiper) this.reelsSwiper.slidePrev();
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        if (this.reelsSwiper) this.reelsSwiper.slideNext();
                        break;
                    case 'ArrowLeft':
                        e.preventDefault();
                        if (this.reelsSwiper) this.reelsSwiper.slidePrev();
                        break;
                    case 'ArrowRight':
                        e.preventDefault();
                        if (this.reelsSwiper) this.reelsSwiper.slideNext();
                        break;
                }
            }
        });

        // Handle video loading and errors
        this.videos.forEach((video, index) => {
            video.addEventListener('ended', () => {
                this.handleVideoEnd(index);
            });

            video.addEventListener('error', (e) => {
                console.error(`Video ${index} loading error:`, e);
            });

            video.addEventListener('loadeddata', () => {
                console.log(`Video ${index} loaded successfully`);
            });
        });

        // Handle page visibility change
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.pauseAllVideos();
                if (this.videosSwiper && this.videosSwiper.autoplay) {
                    // this.videosSwiper.autoplay.stop();
                }
            } else if (!document.hidden) {
                const ugcSection = document.getElementById('ugc-videos');
                if (ugcSection && this.isElementInViewport(ugcSection)) {
                    if (this.videosSwiper && this.videosSwiper.autoplay) {
                        this.videosSwiper.autoplay.start();
                    }
                    setTimeout(() => this.startVideoAutoPlay(), 500);
                }
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            this.handleResize();
        });
    }

    handleSlideChange() {
        console.log('Slide changed to:', this.videosSwiper.activeIndex);

        // Stop all videos first
        this.pauseAllVideos();

        // Start auto-play for current active slide if section is visible
        if (this.isAutoPlaying) {
            setTimeout(() => {
                this.playCurrentSlideVideo();
            }, 500);
        }
    }

    startVideoAutoPlay() {
        if (this.isAutoPlaying) return;

        console.log('Starting video auto-play sequence');
        this.isAutoPlaying = true;
        this.playCurrentSlideVideo();
    }

    stopVideoAutoPlay() {
        console.log('Stopping video auto-play sequence');
        this.isAutoPlaying = false;
        this.pauseAllVideos();

        if (this.autoPlayTimer) {
            clearTimeout(this.autoPlayTimer);
            this.autoPlayTimer = null;
        }
    }

    playCurrentSlideVideo() {
        if (!this.isAutoPlaying || !this.videosSwiper) return;

        const activeIndex = this.videosSwiper.activeIndex;
        const activeSlide = this.videosSwiper.slides[activeIndex];

        if (!activeSlide) return;

        const video = activeSlide.querySelector('.preview-video');
        const card = activeSlide.querySelector('.video-card');

        if (!video || !card) return;

        console.log(`Playing video in slide ${activeIndex}`);

        // Add loading state
        card.classList.add('loading');

        // Play video
        const playPromise = video.play();

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    card.classList.remove('loading');
                    card.classList.add('playing');

                    // Set timer for 4 seconds
                    this.autoPlayTimer = setTimeout(() => {
                        video.pause();
                        video.currentTime = 0;
                        card.classList.remove('playing');
                        if (window.innerWidth > 767)
                            this.nextVideo();
                        // Don't auto-advance slides, let Swiper handle it
                    }, 4000);
                })
                .catch((error) => {
                    console.error('Video auto-play failed:', error);
                    card.classList.remove('loading');
                    this.nextVideo();
                });
        }
    }

    playCurrentVideo() {
        if (!this.isAutoPlaying || this.currentVideoIndex >= this.videos.length) {
            return;
        }

        const currentVideo = this.videos[this.currentVideoIndex];
        const currentCard = this.videoCards[this.currentVideoIndex];

        // Reset all videos first
        this.videos.forEach((video, index) => {
            video.pause();
            video.currentTime = 0;
            this.videoCards[index].classList.remove('playing', 'loading');
        });

        console.log(`Playing video ${this.currentVideoIndex + 1}`);

        // Add loading state
        currentCard.classList.add('loading');

        // Play current video
        const playPromise = currentVideo.play();

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    // Video started playing successfully
                    currentCard.classList.remove('loading');
                    currentCard.classList.add('playing');

                    // Set timer for 4 seconds
                    this.autoPlayTimer = setTimeout(() => {
                        currentVideo.pause();
                        currentVideo.currentTime = 0;
                        currentCard.classList.remove('playing');
                        this.nextVideo();
                    }, 4000);
                })
                .catch((error) => {
                    console.error('Auto-play failed:', error);
                    currentCard.classList.remove('loading');
                    this.nextVideo();
                });
        }
    }

    nextVideo() {
        if (!this.isAutoPlaying) return;

        this.currentVideoIndex++;

        if (this.currentVideoIndex >= this.videos.length) {
            // Loop back to first video
            this.currentVideoIndex = 0;
        }

        // Small delay before next video
        setTimeout(() => {
            this.playCurrentVideo();
        }, 500);
    }

    pauseAllVideos() {
        this.videos.forEach((video, index) => {
            video.pause();
            video.currentTime = 0;

            const card = this.videoCards[index];
            if (card) {
                card.classList.remove('playing', 'loading');
            }
        });

        if (this.autoPlayTimer) {
            clearTimeout(this.autoPlayTimer);
            this.autoPlayTimer = null;
        }
    }

    handleVideoEnd(index) {
        const card = this.videoCards[index];
        if (card) {
            card.classList.remove('playing');
        }
    }

    openReelsModal(startIndex = 0) {
        console.log(`Opening Reels modal starting at video ${startIndex}`);

        // Stop main video auto-play
        this.stopVideoAutoPlay();

        if (this.videosSwiper && this.videosSwiper.autoplay) {
            // this.videosSwiper.autoplay.stop();
        }

        // Populate reels swiper
        this.populateReelsSwiper();

        // Show modal
        this.reelsModal.classList.add('active');
        document.body.style.overflow = 'hidden';

        if (this.reelsSwiper) {
            this.reelsSwiper.destroy(true, true);
        }
        this.initReelsSwiper();

        // Update reels swiper to start at correct index
        if (this.reelsSwiper) {
            this.reelsSwiper.slideTo(startIndex, 0);
            this.currentReelIndex = startIndex;
        }

        // Update progress indicators
        this.updateReelsProgress();

        // Play current reel video
        setTimeout(() => {
            this.playCurrentReelVideo();
        }, 300);

    }

    populateReelsSwiper() {
        const reelsWrapper = document.querySelector('.reels-swiper .swiper-wrapper');
        if (!reelsWrapper) return;

        // Clear existing slides
        reelsWrapper.innerHTML = '';

        // Create slides for each video
        this.videoData.forEach((videoData, index) => {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.innerHTML = `
                <div class="reels-video-wrapper">
                    <div class="reels-video-progress">
                        <div class="reels-progress-bar"></div>
                    </div>
                    <video
                        class="reels-video"
                        preload="none"
                        playsinline
                        webkit-playsinline
                        data-index="${index}"
                        data-ugc-video="${videoData.ugcVideo}"
                        data-video-type="ugc"
                        data-product-id="${videoData.prdId}"
                        poster=""
                        loop
                    >
                        <source src="${videoData.fullSrc}" type="video/mp4">
                        Your browser does not support the video tag.
                    </video>
                    <button class="reels-mute-btn" aria-label="Mute/Unmute">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" fill="none" viewBox="0 0 16 14"><path fill="#FFFFFF" fill-rule="evenodd" d="M.52 8.445a3.304 3.304 0 010-3.557c.264-.418.702-.71 1.206-.806l1.287-.244 2.223-2.529c.334-.38.625-.712.876-.929.226-.196.647-.505 1.167-.327.52.179.634.672.675.96C8 1.33 8 1.763 8 2.258v8.817c0 .495 0 .927-.046 1.245-.041.288-.156.781-.675.96-.52.178-.941-.131-1.167-.327-.25-.217-.542-.549-.876-.929l-.03-.033-2.193-2.496-1.287-.244A1.824 1.824 0 01.52 8.445zM1.562 5.48a2.203 2.203 0 000 2.373c.088.139.234.237.402.268l1.371.26a.984.984 0 01.556.314l2.25 2.559c.27.308.47.535.63.696.013-.22.014-.516.014-.918V2.301c0-.402 0-.698-.014-.918-.16.161-.36.388-.63.696L3.89 4.638a.984.984 0 01-.556.314l-1.371.26a.609.609 0 00-.402.268zm5.496-4.343l-.009.004a.033.033 0 01.009-.004zm-.338-.109a.034.034 0 01-.004-.008l.004.008zm-.004 11.285l.004-.008a.032.032 0 01-.004.008zm.333-.121a.048.048 0 01.009.004s-.003 0-.009-.004zm3.817-8.684a.754.754 0 01.968 0c.955.836 1.493 1.97 1.499 3.151.005 1.183-.524 2.32-1.471 3.162a.754.754 0 01-.967.006.548.548 0 01-.008-.846c.694-.618 1.082-1.45 1.078-2.317-.004-.866-.399-1.696-1.099-2.309a.548.548 0 010-.847zm3.635-1.262c-.268-.328-.7-.328-.968 0a.977.977 0 000 1.185c.7.858 1.095 2.02 1.099 3.233.004 1.213-.384 2.378-1.078 3.242a.978.978 0 00.008 1.186c.269.325.702.32.967-.01.947-1.179 1.476-2.77 1.47-4.425-.005-1.655-.543-3.24-1.498-4.411z" clip-rule="evenodd"></path></svg>
                    </button> 
                    <button class="reels-play-btn playing" aria-label="Play">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.7524 10.0499L3.86361 0.306386C2.30743 -0.489961 0 0.306386 0 2.22699V21.7141C0 23.4941 2.14645 24.5715 3.86361 23.6815L22.7524 13.938C24.4159 13.0479 24.4159 10.94 22.7524 10.0499Z" fill="#FCFCFC"></path></svg>
                    </button> 
                </div>
            `;
            reelsWrapper.appendChild(slide);
        });

        // Update swiper
        if (this.reelsSwiper) {
            this.reelsSwiper.update();
        }

        // Create progress indicators
        this.createReelsProgressIndicators();
    }

    createReelsProgressIndicators() {
        const progressContainer = document.querySelector('.reels-progress');
        if (!progressContainer) return;

        progressContainer.innerHTML = '';

        this.videoData.forEach((_, index) => {
            const dot = document.createElement('div');
            dot.className = 'progress-dot';
            dot.dataset.index = index;
            progressContainer.appendChild(dot);
        });
    }

    handleReelsSlideChange() {
        if (!this.reelsSwiper) return;

        const activeIndex = this.reelsSwiper.activeIndex;
        this.currentReelIndex = activeIndex;

        console.log(`Reels slide changed to: ${activeIndex}`);

        // Pause all reel videos
        this.pauseAllReelVideos();

        // Play current reel video
        setTimeout(() => {
            this.playCurrentReelVideo();
        }, 100);

        this.updateReelsProgress();
    }

    playCurrentReelVideo() {
        const activeSlide = this.reelsSwiper?.slides[this.currentReelIndex];
        if (!activeSlide) return;

        const video = activeSlide.querySelector('.reels-video');
        if (!video) return;

        console.log(`Playing reel video ${this.currentReelIndex}`);

        const wrapper = video.closest('.reels-video-wrapper');
        const bar = wrapper.querySelector('.reels-progress-bar');
        const muteBtn = wrapper.querySelector('.reels-mute-btn');
        const playBtn = wrapper.querySelector('.reels-play-btn');

        // Mute/unmute toggle
        muteBtn.onclick = () => {
            video.muted = !video.muted;
            muteBtn.innerHTML = video.muted
                ? '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" fill="none" viewBox="0 0 16 14"><path fill="#FFFFFF" fill-rule="evenodd" d="M.52 4.888a3.304 3.304 0 000 3.557c.264.418.702.71 1.206.806l1.287.244 2.194 2.496.029.033c.334.38.625.711.876.929.226.196.647.505 1.166.326.52-.178.635-.671.676-.959C8 12.001 8 11.57 8 11.075V2.258c0-.495 0-.927-.046-1.245-.041-.288-.156-.781-.676-.96-.52-.178-.94.131-1.166.327-.25.217-.542.549-.876.929L3.013 3.838l-1.287.244a1.824 1.824 0 00-1.206.806zm1.042 2.965a2.203 2.203 0 010-2.373.609.609 0 01.402-.269l1.371-.26a.984.984 0 00.556-.313l2.25-2.559c.27-.308.47-.535.63-.696.013.22.014.516.014.918v8.731c0 .402-.001.697-.014.918-.16-.161-.36-.388-.63-.697L3.89 8.695a.984.984 0 00-.556-.314l-1.371-.26a.608.608 0 01-.402-.268zm5.496-6.716l-.009.004a.033.033 0 01.009-.004zm-.338-.109a.034.034 0 01-.004-.008l.004.008zm-.004 11.285l.004-.008a.032.032 0 01-.004.008zm.342-.117a.04.04 0 00-.009-.004l.007.003.002.001zm8.785-8.039a.533.533 0 010 .754l-1.756 1.756 1.757 1.756a.533.533 0 11-.754.755L13.333 7.42l-1.757 1.757a.533.533 0 11-.754-.755l1.757-1.756-1.756-1.756a.533.533 0 11.754-.754l1.756 1.756 1.756-1.756a.533.533 0 01.754 0z" clip-rule="evenodd"></path></svg>'
                : '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" fill="none" viewBox="0 0 16 14"><path fill="#FFFFFF" fill-rule="evenodd" d="M.52 8.445a3.304 3.304 0 010-3.557c.264-.418.702-.71 1.206-.806l1.287-.244 2.223-2.529c.334-.38.625-.712.876-.929.226-.196.647-.505 1.167-.327.52.179.634.672.675.96C8 1.33 8 1.763 8 2.258v8.817c0 .495 0 .927-.046 1.245-.041.288-.156.781-.675.96-.52.178-.941-.131-1.167-.327-.25-.217-.542-.549-.876-.929l-.03-.033-2.193-2.496-1.287-.244A1.824 1.824 0 01.52 8.445zM1.562 5.48a2.203 2.203 0 000 2.373c.088.139.234.237.402.268l1.371.26a.984.984 0 01.556.314l2.25 2.559c.27.308.47.535.63.696.013-.22.014-.516.014-.918V2.301c0-.402 0-.698-.014-.918-.16.161-.36.388-.63.696L3.89 4.638a.984.984 0 01-.556.314l-1.371.26a.609.609 0 00-.402.268zm5.496-4.343l-.009.004a.033.033 0 01.009-.004zm-.338-.109a.034.034 0 01-.004-.008l.004.008zm-.004 11.285l.004-.008a.032.032 0 01-.004.008zm.333-.121a.048.048 0 01.009.004s-.003 0-.009-.004zm3.817-8.684a.754.754 0 01.968 0c.955.836 1.493 1.97 1.499 3.151.005 1.183-.524 2.32-1.471 3.162a.754.754 0 01-.967.006.548.548 0 01-.008-.846c.694-.618 1.082-1.45 1.078-2.317-.004-.866-.399-1.696-1.099-2.309a.548.548 0 010-.847zm3.635-1.262c-.268-.328-.7-.328-.968 0a.977.977 0 000 1.185c.7.858 1.095 2.02 1.099 3.233.004 1.213-.384 2.378-1.078 3.242a.978.978 0 00.008 1.186c.269.325.702.32.967-.01.947-1.179 1.476-2.77 1.47-4.425-.005-1.655-.543-3.24-1.498-4.411z" clip-rule="evenodd"></path></svg>';
        };

        playBtn.onclick = () => {
            if (video.paused) {
                video.play();
                playBtn.classList.remove('paused');
                playBtn.classList.add('playing');
            } else {
                video.pause();
                playBtn.classList.remove('playing');
                playBtn.classList.add('paused');
            }
        };

        // On each play, reset & animate the bar
        let progressInterval;

        const updateProgress = () => {
            if (video.duration) {
                const progress = (video.currentTime / video.duration) * 100;
                bar.style.width = progress + '%';
            }
        };

        video.addEventListener('play', () => {
            playBtn.classList.remove('paused');
            playBtn.classList.add('playing');
            bar.style.transition = 'none';
            progressInterval = setInterval(updateProgress, 100);
        });

        video.addEventListener('pause', () => {
            clearInterval(progressInterval);
        });

        video.addEventListener('ended', () => {
            clearInterval(progressInterval);
            bar.style.width = '100%';
        });

        video.play().catch(error => console.error('Reel video play failed:', error));
    }

    pauseAllReelVideos() {
        const reelVideos = document.querySelectorAll('.reels-video');
        reelVideos.forEach(video => {
            video.pause();
        });
    }

    updateReelsProgress() {
        const progressDots = document.querySelectorAll('.progress-dot');
        progressDots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentReelIndex);
        });
    }

    closeReelsModal() {
        console.log('Closing Reels modal');

        // Pause all reel videos
        this.pauseAllReelVideos();

        // Hide modal
        this.reelsModal.classList.remove('active');
        document.body.style.overflow = '';

        // Restart main auto-play if section is still visible
        setTimeout(() => {
            const ugcSection = document.getElementById('ugc-videos');
            if (ugcSection && this.isElementInViewport(ugcSection)) {
                if (this.videosSwiper && this.videosSwiper.autoplay) {
                    this.videosSwiper.autoplay.start();
                }
                this.startVideoAutoPlay();
            }
        }, 500);
    }

    isElementInViewport(element) {
        const rect = element.getBoundingClientRect();
        const threshold = 0.3;
        const elementHeight = rect.bottom - rect.top;
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

        return visibleHeight / elementHeight >= threshold;
    }

    handleResize() {
        console.log('Window resized, updating swipers');

        if (this.videosSwiper) {
            this.videosSwiper.update();
        }

        if (this.reelsSwiper) {
            this.reelsSwiper.update();
        }
    }

    // Public methods for external control
    pauseAutoPlay() {
        this.stopVideoAutoPlay();
        if (this.videosSwiper && this.videosSwiper.autoplay) {
            // this.videosSwiper.autoplay.stop();
        }
    }

    resumeAutoPlay() {
        const ugcSection = document.getElementById('ugc-videos');
        if (ugcSection && this.isElementInViewport(ugcSection)) {
            if (this.videosSwiper && this.videosSwiper.autoplay) {
                this.videosSwiper.autoplay.start();
            }
            this.startVideoAutoPlay();
        }
    }

    goToSlide(index) {
        if (this.videosSwiper && index >= 0 && index < this.videoData.length) {
            this.videosSwiper.slideTo(index);
        }
    }

    getCurrentSlide() {
        return this.videosSwiper ? this.videosSwiper.activeIndex : 0;
    }

    destroy() {
        console.log('Destroying UGC Video Section');

        this.stopVideoAutoPlay();

        if (this.videosSwiper) {
            this.videosSwiper.destroy(true, true);
        }

        if (this.reelsSwiper) {
            this.reelsSwiper.destroy(true, true);
        }

        // Clean up event listeners if needed
        this.pauseAllVideos();
        this.pauseAllReelVideos();
    }
}

// Initialize when DOM is ready
let ugcVideoSection;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ugcVideoSection = new UGCVideoSectionSwiper();
    });
} else {
    ugcVideoSection = new UGCVideoSectionSwiper();
}

// Expose to global scope for debugging
window.ugcVideoSection = ugcVideoSection;

// Handle page unload
window.addEventListener('beforeunload', () => {
    if (ugcVideoSection) {
        ugcVideoSection.destroy();
    }
});

// Additional utility functions for enhanced functionality
window.UGCVideoUtils = {
    // Manually trigger modal opening
    openVideo: (index) => {
        if (ugcVideoSection) {
            ugcVideoSection.openReelsModal(index);
        }
    },

    // Get current playing video info
    getCurrentVideoInfo: () => {
        if (ugcVideoSection) {
            const currentIndex = ugcVideoSection.getCurrentSlide();
            return ugcVideoSection.videoData[currentIndex];
        }
        return null;
    },

    // Control autoplay externally
    toggleAutoplay: () => {
        if (ugcVideoSection) {
            if (ugcVideoSection.isAutoPlaying) {
                ugcVideoSection.pauseAutoPlay();
            } else {
                ugcVideoSection.resumeAutoPlay();
            }
        }
    }
};




// Initialize page video data structure
let pageVideoData = {
    pageName: document.title,
    pageUrl: window.location.href,
    pageType: window.location.pathname.includes('/products/') ? 'product' :
        window.location.pathname.includes('/collections/') ? 'collection' :
            window.location.pathname.includes('/cart') ? 'cart' : 'other',
    videos: {},
    sessionStartTime: new Date()
};

// Main tracking function
function trackUGCVideoPerformance(video, videoId, action) {
    const videoUrl = video.src || video.currentSrc;
    const currentTime = video.currentTime;
    const duration = video.duration;

    // Initialize video tracking if not exists
    if (!pageVideoData.videos[videoId]) {
        pageVideoData.videos[videoId] = {
            videoUrl: videoUrl,
            videoTitle: video.dataset.videoTitle || 'UGC Video',
            videoThumbnail: video.poster || 'N/A',
            totalWatchTime: 0,
            maxProgress: 0,
            startTime: null,
            sessionsCount: 0,
            interactions: [],
            firstPlayTime: new Date().toISOString()
        };
    }

    const videoData = pageVideoData.videos[videoId];
    const watchedPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Track maximum progress
    if (watchedPercentage > videoData.maxProgress) {
        videoData.maxProgress = watchedPercentage;
    }

    switch (action) {
        case 'play':
            videoData.startTime = currentTime;
            videoData.sessionsCount++;

            // Send to GA4 with page context
            gtag('event', 'ugc_video_play', {
                'page_title': pageVideoData.pageName,
                'page_url': pageVideoData.pageUrl,
                'page_type': pageVideoData.pageType,
                'video_id': videoId,
                'video_url': videoUrl,
                'video_title': videoData.videoTitle,
                'current_time': currentTime.toFixed(2),
                'video_duration': duration.toFixed(2),
                'session_number': videoData.sessionsCount,
                'custom_parameter': 'ugc_page_tracking'
            });

            console.log(`[UGC Tracking] Video play: ${videoUrl} at ${currentTime.toFixed(2)}s`);
            break;

        case 'pause':
            if (videoData.startTime !== null) {
                const sessionWatchTime = currentTime - videoData.startTime;
                videoData.totalWatchTime += sessionWatchTime;
                videoData.startTime = null;
            }

            gtag('event', `ugc_video_${action}`, {
                'page_title': pageVideoData.pageName,
                'page_url': pageVideoData.pageUrl,
                'page_type': pageVideoData.pageType,
                'video_id': videoId,
                'video_url': videoUrl,
                'video_title': videoData.videoTitle,
                'total_watch_time': videoData.totalWatchTime.toFixed(2),
                'max_progress': videoData.maxProgress.toFixed(2),
                'sessions_count': videoData.sessionsCount,
                'current_time': currentTime.toFixed(2),
                'video_duration': duration.toFixed(2),
                'custom_parameter': 'ugc_page_tracking'
            });

            console.log(`[UGC Tracking] Video ${action}: ${videoId} - Total watch: ${videoData.totalWatchTime.toFixed(2)}s`);
        case 'ended':
            //   if (videoData.startTime !== null) {
            //     const sessionWatchTime = currentTime - videoData.startTime;
            //     videoData.totalWatchTime += sessionWatchTime;
            //     videoData.startTime = null;
            //   }

            //   gtag('event', `ugc_video_${action}`, {
            //     'page_title': pageVideoData.pageName,
            //     'page_url': pageVideoData.pageUrl,
            //     'page_type': pageVideoData.pageType,
            //     'video_id': videoId,
            //     'video_url': videoUrl,
            //     'video_title': videoData.videoTitle,
            //     'total_watch_time': videoData.totalWatchTime.toFixed(2),
            //     'max_progress': videoData.maxProgress.toFixed(2),
            //     'sessions_count': videoData.sessionsCount,
            //     'current_time': currentTime.toFixed(2),
            //     'video_duration': duration.toFixed(2),
            //     'custom_parameter': 'ugc_page_tracking'
            //   });

            //   console.log(`[UGC Tracking] Video ${action}: ${videoId} - Total watch: ${videoData.totalWatchTime.toFixed(2)}s`);
            break;

        case 'milestone':
            const milestones = [25, 50, 75, 100];
            const currentMilestone = milestones.find(m =>
                watchedPercentage >= m && !videoData[`milestone_${m}`]
            );

            if (currentMilestone) {
                videoData[`milestone_${currentMilestone}`] = true;

                gtag('event', 'ugc_video_milestone', {
                    'page_title': pageVideoData.pageName,
                    'page_url': pageVideoData.pageUrl,
                    'page_type': pageVideoData.pageType,
                    'video_id': videoId,
                    'video_url': videoUrl,
                    'video_title': videoData.videoTitle,
                    'milestone_percent': currentMilestone,
                    'watch_time_at_milestone': videoData.totalWatchTime.toFixed(2),
                    'max_progress': videoData.maxProgress.toFixed(2),
                    'current_time': currentTime.toFixed(2),
                    'custom_parameter': 'ugc_page_tracking'
                });

                console.log(`[UGC Tracking] Milestone reached: ${videoId} - ${currentMilestone}%`);
            }
            break;
    }

    // Send page-level summary every 60 seconds
    if (videoData.totalWatchTime > 0 &&
        Math.floor(videoData.totalWatchTime) % 60 === 0 &&
        !videoData[`sent_minute_${Math.floor(videoData.totalWatchTime / 60)}`]) {

        videoData[`sent_minute_${Math.floor(videoData.totalWatchTime / 60)}`] = true;

        const totalVideosWatched = Object.values(pageVideoData.videos).filter(v => v.totalWatchTime > 0).length;
        const avgWatchTime = totalVideosWatched > 0 ?
            (Object.values(pageVideoData.videos).reduce((sum, v) => sum + v.totalWatchTime, 0) / totalVideosWatched).toFixed(2) :
            0;

        gtag('event', 'ugc_page_video_summary', {
            'page_title': pageVideoData.pageName,
            'page_url': pageVideoData.pageUrl,
            'page_type': pageVideoData.pageType,
            'videos_on_page': Object.keys(pageVideoData.videos).length,
            'total_videos_watched': totalVideosWatched,
            'average_watch_time': avgWatchTime,
            'custom_parameter': 'ugc_page_summary'
        });

        console.log(`[UGC Tracking] Page summary - Videos watched: ${totalVideosWatched}, Avg time: ${avgWatchTime}s`);
    }
}

// Initialize tracking for all UGC videos
function initializeUGCTracking() {
    const videos = document.querySelectorAll('video[data-ugc-video], video[data-video-type="ugc"]');

    if (videos.length === 0) {
        console.log('[UGC Tracking] No UGC videos found on this page');
        return;
    }

    console.log(`[UGC Tracking] Initializing tracking for ${videos.length} video(s)`);

    videos.forEach((video, index) => {
        const videoId = video.dataset.ugcVideo ||
            video.dataset.videoId ||
            video.src?.split('/').pop()?.split('.')[0] ||
            `ugc_${index}_${Math.random().toString(36).substr(2, 9)}`;

        console.log(`[UGC Tracking] Added video: ${videoId}`);

        video.addEventListener('play', () => trackUGCVideoPerformance(video, videoId, 'play'), { once: false });
        video.addEventListener('pause', () => trackUGCVideoPerformance(video, videoId, 'pause'), { once: false });
        video.addEventListener('ended', () => trackUGCVideoPerformance(video, videoId, 'ended'), { once: false });

        // Use throttled timeupdate for better performance
        let lastMilestoneCheck = 0;
        video.addEventListener('timeupdate', () => {
            const now = Date.now();
            if (now - lastMilestoneCheck > 500) { // Check every 500ms max
                trackUGCVideoPerformance(video, videoId, 'milestone');
                lastMilestoneCheck = now;
            }
        }, { once: false });
    });
}

// Run on page load and for dynamic content
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeUGCTracking);
} else {
    initializeUGCTracking();
}

// Re-initialize for dynamically added videos (for single-page apps)
const observer = new MutationObserver(() => {
    initializeUGCTracking();
});

observer.observe(document.body, {
    childList: true,
    subtree: true
});

// Track when user leaves page
window.addEventListener('beforeunload', () => {
    Object.entries(pageVideoData.videos).forEach(([videoId, data]) => {
        if (data.startTime !== null) {
            gtag('event', 'ugc_video_exit', {
                'page_title': pageVideoData.pageName,
                'page_url': pageVideoData.pageUrl,
                'page_type': pageVideoData.pageType,
                'video_id': videoId,
                'video_url': data.videoUrl,
                'video_title': data.videoTitle,
                'total_watch_time': data.totalWatchTime.toFixed(2),
                'max_progress': data.maxProgress.toFixed(2),
                'sessions_count': data.sessionsCount,
                'custom_parameter': 'ugc_page_tracking'
            });
        }
    });
});






// // Initialize page and video tracking
// let pageVideoData = {
//   pageName: document.title,
//   pageUrl: window.location.href,
//   pageType: window.location.pathname.includes('/products/') ? 'product' : 
//            window.location.pathname.includes('/collections/') ? 'collection' : 'other',
//   videos: {}
// };

// function trackUGCVideoPerformance(video, videoId, action) {
//   const videoUrl = video.src || video.currentSrc;
//   const currentTime = video.currentTime;
//   const duration = video.duration;
  
//   // Initialize video tracking if not exists
//   if (!pageVideoData.videos[videoId]) {
//     pageVideoData.videos[videoId] = {
//       videoUrl: videoUrl,
//       videoTitle: video.dataset.videoTitle || 'UGC Video',
//       totalWatchTime: 0,
//       maxProgress: 0,
//       startTime: null,
//       sessionsCount: 0,
//       interactions: []
//     };
//   }
  
//   const videoData = pageVideoData.videos[videoId];
//   const watchedPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  
//   // Track maximum progress
//   if (watchedPercentage > videoData.maxProgress) {
//     videoData.maxProgress = watchedPercentage;
//   }
  
//   switch(action) {
//     case 'play':
//       videoData.startTime = currentTime;
//       videoData.sessionsCount++;
      
//       // Send to GA4 with page context
//       gtag('event', 'ugc_video_play', {
//         'page_title': pageVideoData.pageName,
//         'page_url': pageVideoData.pageUrl,
//         'page_type': pageVideoData.pageType,
//         'video_id': videoId,
//         'video_url': videoUrl,
//         'video_title': videoData.videoTitle,
//         'current_time': currentTime.toFixed(2),
//         'session_number': videoData.sessionsCount,
//         'custom_parameter': 'ugc_page_tracking'
//       });
//       break;
      
//     case 'pause':
//     case 'ended':
//       if (videoData.startTime !== null) {
//         const sessionWatchTime = currentTime - videoData.startTime;
//         videoData.totalWatchTime += sessionWatchTime;
//         videoData.startTime = null;
//       }
      
//       gtag('event', `ugc_video_${action}`, {
//         'page_title': pageVideoData.pageName,
//         'page_url': pageVideoData.pageUrl,
//         'page_type': pageVideoData.pageType,
//         'video_id': videoId,
//         'video_url': videoUrl,
//         'video_title': videoData.videoTitle,
//         'total_watch_time': videoData.totalWatchTime.toFixed(2),
//         'max_progress': videoData.maxProgress.toFixed(2),
//         'sessions_count': videoData.sessionsCount,
//         'custom_parameter': 'ugc_page_tracking'
//       });
//       break;
      
//     case 'milestone':
//       const milestones = [25, 50, 75, 100];
//       const currentMilestone = milestones.find(m => 
//         watchedPercentage >= m && !videoData[`milestone_${m}`]
//       );
      
//       if (currentMilestone) {
//         videoData[`milestone_${currentMilestone}`] = true;
        
//         gtag('event', 'ugc_video_milestone', {
//           'page_title': pageVideoData.pageName,
//           'page_url': pageVideoData.pageUrl,
//           'page_type': pageVideoData.pageType,
//           'video_id': videoId,
//           'video_url': videoUrl,
//           'milestone_percent': currentMilestone,
//           'watch_time_at_milestone': videoData.totalWatchTime.toFixed(2),
//           'custom_parameter': 'ugc_page_tracking'
//         });
//       }
//       break;
//   }
  
//   // Send page-level summary every minute
//   if (videoData.totalWatchTime > 0 && 
//       Math.floor(videoData.totalWatchTime) % 60 === 0 && 
//       !videoData[`sent_minute_${Math.floor(videoData.totalWatchTime / 60)}`]) {
    
//     videoData[`sent_minute_${Math.floor(videoData.totalWatchTime / 60)}`] = true;
    
//     gtag('event', 'ugc_page_video_summary', {
//       'page_title': pageVideoData.pageName,
//       'page_url': pageVideoData.pageUrl,
//       'page_type': pageVideoData.pageType,
//       'videos_on_page': Object.keys(pageVideoData.videos).length,
//       'total_videos_watched': Object.values(pageVideoData.videos).filter(v => v.totalWatchTime > 0).length,
//       'average_watch_time': (Object.values(pageVideoData.videos).reduce((sum, v) => sum + v.totalWatchTime, 0) / Object.keys(pageVideoData.videos).length).toFixed(2),
//       'custom_parameter': 'ugc_page_summary'
//     });
//   }
// }

// // Initialize tracking for all UGC videos
// function initializeUGCTracking() {
//   document.querySelectorAll('video[data-ugc-video], video[data-video-type="ugc"]').forEach(video => {
//     const videoId = video.dataset.ugcVideo || 
//                    video.dataset.videoId || 
//                    video.src?.split('/').pop()?.split('.')[0] || 
//                    `ugc_${Math.random().toString(36).substr(2, 9)}`;
    
//     video.addEventListener('play', () => trackUGCVideoPerformance(video, videoId, 'play'));
//     video.addEventListener('pause', () => trackUGCVideoPerformance(video, videoId, 'pause'));
//     video.addEventListener('ended', () => trackUGCVideoPerformance(video, videoId, 'ended'));
    
//     video.addEventListener('timeupdate', () => {
//       trackUGCVideoPerformance(video, videoId, 'milestone');
//     });
//   });
// }

// // Run on page load and for dynamic content
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', initializeUGCTracking);
// } else {
//   initializeUGCTracking();
// }

// // Track when user leaves page
// window.addEventListener('beforeunload', () => {
//   Object.entries(pageVideoData.videos).forEach(([videoId, data]) => {
//     if (data.startTime !== null) {
//       gtag('event', 'ugc_video_exit', {
//         'page_title': pageVideoData.pageName,
//         'page_url': pageVideoData.pageUrl,
//         'video_id': videoId,
//         'total_watch_time': data.totalWatchTime.toFixed(2),
//         'max_progress': data.maxProgress.toFixed(2),
//         'custom_parameter': 'ugc_page_tracking'
//       });
//     }
//   });
// });